import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, DataTable, EmptyState, StatusPill, TimeRangePicker, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useSlaDashboardOverview, useSlaDashboardServices, type SlaDashboardServiceRow } from '@/api/services';
import { EntityLink } from '@/components/entity-link';
import { TableLink } from '@/components/table-link';

/**
 * Build a local YYYY-MM-DD string from a Date without UTC shift (Asia/Saigon UTC+7).
 * Using toISOString().slice(0,10) shifts the date back one day in UTC+7 — see ui-conventions §4.
 */
function toLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function defaultDates() {
  const today = new Date();
  const start = new Date(today);
  start.setMonth(start.getMonth() - 3);
  return { start: toLocalDate(start), end: toLocalDate(today) };
}

/** Format minutes into a human-readable string (e.g. "2h 30m"). */
function fmtMinutes(min: number): string {
  if (!min) return '0 min';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const SERVICE_COLUMNS: ColumnDef<SlaDashboardServiceRow, unknown>[] = [
  {
    id: 'service_id',
    header: 'ID',
    cell: ({ row }) => (
      <TableLink to={`/services/${row.original.service_id}?tab=sla`} title={String(row.original.service_id)}>
        {row.original.service_id}
      </TableLink>
    ),
  },
  {
    id: 'service_name',
    header: 'Service',
    cell: ({ row }) => (
      <span
        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase' }}
        title={row.original.service_name}
      >
        {row.original.service_name}
      </span>
    ),
  },
  {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) => (
      <EntityLink
        to={`/customers/${row.original.customer_id}`}
        id={row.original.customer_id}
        name={row.original.customer_name}
      />
    ),
  },
  {
    id: 'ss_target_uptime',
    header: 'Target',
    cell: ({ row }) => `${row.original.ss_target_uptime}%`,
  },
  {
    id: 'actual_uptime',
    header: 'Actual uptime',
    cell: ({ row }) => `${row.original.actual_uptime.toFixed(3)}%`,
  },
  {
    id: 'ss_sla_downtime_minutes',
    header: 'SLA downtime',
    cell: ({ row }) => fmtMinutes(row.original.ss_sla_downtime_minutes),
  },
  {
    id: 'ss_incident_count',
    header: 'Incidents',
    cell: ({ row }) => row.original.ss_incident_count,
  },
  {
    id: 'sla_met',
    header: 'Status',
    cell: ({ row }) => (
      <StatusPill tone={row.original.sla_met ? 'success' : 'danger'}>
        {row.original.sla_met ? 'Met' : 'Breached'}
      </StatusPill>
    ),
  },
];

/**
 * SLA Dashboard — period-scoped KPI view for all SLA-attached services.
 * Endpoints (both verified live 200):
 *   GET /sla/dashboard/overview — aggregate KPIs
 *   GET /sla/dashboard/services — per-service uptime table
 *
 * The user selects a date range (defaults to last 3 months). KPI stat cards
 * show totals; the services table shows per-service compliance.
 */
export default function SlaDashboardPage() {
  const navigate = useNavigate();
  const { start: defaultStart, end: defaultEnd } = defaultDates();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const datesValid = !!startDate && !!endDate && startDate <= endDate;

  const overview = useSlaDashboardOverview(startDate, endDate, datesValid);
  const services = useSlaDashboardServices(startDate, endDate, datesValid);

  const kpi = overview.data;
  const rows = services.data ?? [];

  return (
    <Container size="full">
      <PageHeader
        title="SLA Dashboard"
        description="Service-level agreement compliance overview across all SLA-attached services."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Period selector — @ui TimeRangePicker (absolute From/To; dates only) */}
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
                Period
              </span>
              <TimeRangePicker
                showLive={false}
                presets={[]}
                from={startDate}
                to={endDate}
                triggerLabel={startDate && endDate ? `${startDate} → ${endDate}` : 'Select period'}
                onApply={(v) => {
                  if (v.from && v.to) {
                    setStartDate(v.from.slice(0, 10));
                    setEndDate(v.to.slice(0, 10));
                  }
                }}
              />
            </div>
          }
        />

        {/* KPI stat band */}
        {overview.isLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 80 }} />
            ))}
          </div>
        ) : overview.isError ? (
          <Alert tone="danger" title="Failed to load SLA overview" />
        ) : kpi ? (
          <>
            <Card flat>
              <CardBody>
                <StatGroup columns={4}>
                  <Stat label="Services with SLA" value={kpi.total_services_with_sla} />
                  <Stat
                    label="Meeting SLA"
                    value={kpi.services_meeting_sla}
                    dotColor="var(--vn-success-solid)"
                  />
                  <Stat
                    label="Breaching SLA"
                    value={kpi.services_breaching_sla}
                    dotColor={kpi.services_breaching_sla > 0 ? 'var(--vn-danger-solid)' : 'var(--vn-border-subtle)'}
                  />
                  <Stat label="Total incidents" value={kpi.total_incidents} />
                </StatGroup>
              </CardBody>
            </Card>
            <Card flat>
              <CardBody>
                <StatGroup columns={4}>
                  <Stat label="Avg uptime" value={`${kpi.avg_uptime_percent.toFixed(3)}%`} />
                  <Stat label="Provider downtime" value={fmtMinutes(kpi.total_provider_downtime)} />
                  <Stat label="Customer downtime" value={fmtMinutes(kpi.total_customer_downtime)} />
                  <Stat label="Scheduled downtime" value={fmtMinutes(kpi.total_scheduled_downtime)} />
                </StatGroup>
              </CardBody>
            </Card>
          </>
        ) : null}

      {/* Per-service compliance table */}
      <Card flat>
        <CardHeader>
          <CardTitle>Services</CardTitle>
        </CardHeader>
        <CardBody style={{ padding: 0 }}>
          {services.isError ? (
            <div style={{ padding: 'var(--vn-space-4)' }}>
              <Alert tone="danger" title="Failed to load SLA services" />
            </div>
          ) : services.isLoading ? (
            <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </div>
          ) : (
            <DataTable
              columns={SERVICE_COLUMNS}
              data={rows}
              getRowId={(r) => String(r.service_id)}
              onRowClick={(r) => navigate(`/services/${r.service_id}?tab=sla`)}
              emptyState={
                <EmptyState
                  icon={<ShieldCheck size={24} />}
                  title="No SLA services found"
                  description="No SLA-attached services found for the selected period."
                />
              }
            />
          )}
        </CardBody>
      </Card>
      </div>
    </Container>
  );
}
